import { db } from '../firebase';
import { doc, updateDoc, increment, collection, addDoc, getDoc, setDoc, runTransaction, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { recordCreditUsage as recordMetricsCreditUsage } from './metricsService';
import { getUserSubscription } from './subscriptionService';
import { recordCreditUsage as recordOptimizedCreditUsage } from './optimizedCreditService';

// Initialize or get user credits
export const initializeUserCredits = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();

    if (!userData?.credits) {
      // Initialize credits if they don't exist
      await updateDoc(userRef, {
        credits: {
          total: 100, // Legacy field - keeping for backward compatibility
          used: 0,
          monthly: 100, // New field for monthly subscription credits
          flex: 0, // New field for flex credits
          lastRefillDate: new Date().toISOString()
        }
      });
      return true;
    }

    // Ensure all required fields exist
    const updates = {};
    if (userData.credits.used === undefined) {
      updates['credits.used'] = 0;
    }
    if (userData.credits.total === undefined) {
      updates['credits.total'] = 100;
    }
    if (userData.credits.monthly === undefined) {
      updates['credits.monthly'] = userData.credits.total || 100;
    }
    if (userData.credits.flex === undefined) {
      updates['credits.flex'] = 0;
    }
    if (userData.credits.lastRefillDate === undefined) {
      updates['credits.lastRefillDate'] = new Date().toISOString();
    }

    // Apply any missing fields
    if (Object.keys(updates).length > 0) {
      await updateDoc(userRef, updates);
    }

    return true;
  } catch (error) {
    console.error('Error initializing user credits:', error);
    throw error;
  }
};

// Record a successful product push with shop-specific and aggregate metrics
export const recordPushToShopify = async (userId, shopId, productId) => {
  try {
    // 1. Record in user's push history
    const pushRef = collection(db, 'users', userId, 'productPushes');
    await addDoc(pushRef, {
      productId,
      shopId,
      timestamp: new Date().toISOString()
    });
    
    // 2. Update shop-specific push metrics
    if (shopId) {
      const shopRef = doc(db, 'shops', shopId);
      const shopDoc = await getDoc(shopRef);
      
      if (shopDoc.exists()) {
        // Get current date for monthly and daily stats
        const currentDate = new Date();
        const monthKey = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}`;
        const dayKey = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
        
        // Update shop push metrics
        await updateDoc(shopRef, {
          'metrics.pushes.total': increment(1),
          [`metrics.pushes.monthly.${monthKey}`]: increment(1),
          [`metrics.pushes.daily.${dayKey}`]: increment(1),
          'metrics.lastPushDate': new Date().toISOString()
        });
        
        console.log('Updated shop push metrics:', {
          shopId,
          monthKey,
          dayKey
        });
      }
    }
    
    // 3. Update aggregate user metrics
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'pushHistory.totalPushes': increment(1)
    });
    
    return true;
  } catch (error) {
    console.error('Error recording product push:', error);
    throw error;
  }
};

// Record a successful product push and deduct credits with transaction
const recordPushWithTransaction = async (userId, shopId, productData) => {
  await runTransaction(db, async (transaction) => {
    // 1. Update user credits and push history
    const userRef = doc(db, 'users', userId);
    const userDoc = await transaction.get(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }

    const userData = userDoc.data();
    
    // Initialize credits if they don't exist
    if (!userData?.credits) {
      await initializeUserCredits(userId);
      throw new Error('Retrying after credit initialization');
    }

    // Ensure credits.used exists and is a number
    if (typeof userData.credits.used !== 'number') {
      userData.credits.used = 0;
    }

    const currentDate = new Date();
    const currentMonth = currentDate.toISOString().slice(0, 7); // Format: YYYY-MM
    const monthKey = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}`;
    const dayKey = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
    const creditsBeforeUpdate = { ...userData.credits };
    
    // Calculate new values
    const newUsedCredits = userData.credits.used + 1;
    const newTotalPushes = (userData.pushHistory?.totalPushes || 0) + 1;
    const newMonthlyPushes = (userData.pushHistory?.monthlyPushes?.[currentMonth] || 0) + 1;
    const newShopPushes = (userData.pushHistory?.shopPushes?.[shopId] || 0) + 1;

    // Determine which credit pool to deduct from
    let monthlyCreditsUpdate = {};
    let flexCreditsUpdate = {};
    
    // Check if we have monthly credits available
    if (userData.credits.monthly > 0) {
      monthlyCreditsUpdate = { 'credits.monthly': userData.credits.monthly - 1 };
    } else if (userData.credits.flex > 0) {
      // Use flex credits if monthly credits are depleted
      flexCreditsUpdate = { 'credits.flex': userData.credits.flex - 1 };
    }
    
    // For backward compatibility, also update the total field
    const totalCreditsUpdate = { 'credits.total': Math.max(0, (userData.credits.total || 0) - 1) };

    // Update user document in transaction
    transaction.update(userRef, {
      'credits.used': newUsedCredits,
      'pushHistory.totalPushes': newTotalPushes,
      [`pushHistory.monthlyPushes.${currentMonth}`]: newMonthlyPushes,
      [`pushHistory.shopPushes.${shopId}`]: newShopPushes,
      ...monthlyCreditsUpdate,
      ...flexCreditsUpdate,
      ...totalCreditsUpdate
    });
    
    // 2. Update shop-specific metrics if shopId is provided
    if (shopId) {
      const shopRef = doc(db, 'shops', shopId);
      const shopDoc = await transaction.get(shopRef);
      
      if (shopDoc.exists()) {
        // Initialize metrics object if it doesn't exist
        const shopData = shopDoc.data();
        if (!shopData.metrics) {
          transaction.set(shopRef, { metrics: {} }, { merge: true });
        }
        
        // Update shop push metrics
        transaction.update(shopRef, {
          'metrics.pushes.total': increment(1),
          [`metrics.pushes.monthly.${monthKey}`]: increment(1),
          [`metrics.pushes.daily.${dayKey}`]: increment(1),
          'metrics.lastPushDate': new Date().toISOString()
        });
      }
    }

    // Add push history record
    const historyRef = collection(db, 'pushHistory');
    transaction.set(doc(historyRef), {
      userId,
      shopId,
      productId: productData.id,
      productTitle: productData.title || 'Unknown',
      timestamp: new Date().toISOString(),
      creditsUsed: 1,
      pushType: productData.updateType || 'single',
      beforeCredits: creditsBeforeUpdate,
      afterCredits: {
        ...userData.credits,
        used: newUsedCredits,
        monthly: monthlyCreditsUpdate['credits.monthly'] !== undefined 
          ? monthlyCreditsUpdate['credits.monthly'] 
          : userData.credits.monthly,
        flex: flexCreditsUpdate['credits.flex'] !== undefined 
          ? flexCreditsUpdate['credits.flex'] 
          : userData.credits.flex,
        total: totalCreditsUpdate['credits.total']
      },
      creditSource: monthlyCreditsUpdate['credits.monthly'] !== undefined 
        ? 'monthly' 
        : (flexCreditsUpdate['credits.flex'] !== undefined ? 'flex' : 'legacy')
    });
  });
};

// Record a successful product push and deduct credits
export const deductCreditsAndRecordPush = async (userId, shopId, productData) => {
  try {
    console.log('Recording product push and deducting credits:', { userId, shopId, productId: productData.id });

    try {
      // Try using the transaction-based approach first
      await recordPushWithTransaction(userId, shopId, productData);
    } catch (error) {
      if (error.message === 'Retrying after credit initialization') {
        await recordPushWithTransaction(userId, shopId, productData);
      } else {
        // Fall back to the old method if transaction fails
        const userRef = doc(db, 'users', userId);
        
        // Get current credits before update
        let userDoc = await getDoc(userRef);
        let userData = userDoc.data();
        
        // Initialize credits if they don't exist
        if (!userData?.credits) {
          await initializeUserCredits(userId);
          userDoc = await getDoc(userRef);
          userData = userDoc.data();
        }

        // Ensure credits.used exists and is a number
        if (typeof userData.credits.used !== 'number') {
          await updateDoc(userRef, { 'credits.used': 0 });
          userDoc = await getDoc(userRef);
          userData = userDoc.data();
        }

        const currentMonth = new Date().toISOString().slice(0, 7);
        const creditsBeforeUpdate = { ...userData.credits };
        
        // Determine which credit pool to deduct from
        let updates = {
          'credits.used': increment(1),
          'pushHistory.totalPushes': increment(1),
          [`pushHistory.monthlyPushes.${currentMonth}`]: increment(1),
          [`pushHistory.shopPushes.${shopId}`]: increment(1)
        };
        
        let creditSource = 'legacy';
        
        // Check if we have monthly credits available
        if (userData.credits.monthly > 0) {
          updates['credits.monthly'] = increment(-1);
          creditSource = 'monthly';
        } else if (userData.credits.flex > 0) {
          // Use flex credits if monthly credits are depleted
          updates['credits.flex'] = increment(-1);
          creditSource = 'flex';
        }
        
        // For backward compatibility, also update the total field
        updates['credits.total'] = Math.max(0, (userData.credits.total || 0) - 1);

        await updateDoc(userRef, updates);

        // Record push history
        await addDoc(collection(db, 'pushHistory'), {
          userId,
          shopId,
          productId: productData.id,
          productTitle: productData.title || 'Unknown',
          timestamp: new Date().toISOString(),
          creditsUsed: 1,
          pushType: productData.updateType || 'single',
          beforeCredits: creditsBeforeUpdate,
          afterCredits: {
            ...userData.credits,
            used: userData.credits.used + 1,
            monthly: creditSource === 'monthly' 
              ? userData.credits.monthly - 1 
              : userData.credits.monthly,
            flex: creditSource === 'flex' 
              ? userData.credits.flex - 1 
              : userData.credits.flex,
            total: Math.max(0, (userData.credits.total || 0) - 1)
          },
          creditSource
        });
      }
    }

    // Record metrics using the optimized credit service (non-blocking)
    try {
      // Use the optimized credit service for tracking
      await recordOptimizedCreditUsage(userId, 1, {
        shopId,
        productId: productData.id,
        productTitle: productData.title || 'Unknown',
        type: 'product_push',
        usageType: 'other',
        pushType: productData.updateType || 'single'
      });
    } catch (trackingError) {
      console.error('Error recording optimized credit usage:', trackingError);
      // Continue even if tracking fails, as the credits have been deducted
    }
    
    // Also update shop metrics (non-blocking and won't affect main flow if it fails)
    recordMetricsCreditUsage(userId, shopId, 1).catch(error => {
      console.error('Error recording shop metrics:', error);
    });

    console.log('Product push recorded and credits deducted successfully');
    return true;

  } catch (error) {
    console.error('Error recording product push and deducting credits:', error);
    throw error;
  }
};

// Record credit usage for templates and other features
// This is a wrapper around the optimized credit service's recordCreditUsage function
export const recordCreditUsage = async (userId, templateId, params = {}) => {
  try {
    // Double-check if userId is in params and use it if the first parameter is missing
    if (!userId && params.userId) {
      userId = params.userId;
      console.log('Using userId from params object instead of first parameter');
    }
    
    const { 
      productId = 'unknown', 
      shopId = 'unknown', 
      templateType = 'product',
      model = 'unknown',
      inputTokens = 0,
      outputTokens = 0
    } = params;
    
    if (!userId) {
      console.error('recordCreditUsage called with no userId');
      console.trace('Stack trace for missing userId:');
      return false;
    }
    
    console.log(`Recording credit usage for user ${userId}, template: ${templateId}, product: ${productId}`);
    
    // Calculate credits based on model and tokens
    // Default to 1 credit if no token information is available
    let creditsUsed = 1;
    
    if (inputTokens > 0 || outputTokens > 0) {
      // More sophisticated credit calculation based on tokens and model
      // This can be expanded based on different pricing tiers
      if (model.includes('gpt-4')) {
        creditsUsed = Math.max(1, Math.ceil((inputTokens / 1000 * 0.03) + (outputTokens / 1000 * 0.06)));
      } else {
        // gpt-3.5-turbo and others
        creditsUsed = Math.max(1, Math.ceil((inputTokens / 1000 * 0.0015) + (outputTokens / 1000 * 0.002)));
      }
    }
    
    // Deduct credits from the user's account
    await deductCredits(userId, creditsUsed);
    
    // Use the optimized credit service for tracking
    await recordOptimizedCreditUsage(userId, creditsUsed, {
      usageType: 'template',
      templateId,
      productId,
      shopId,
      templateType,
      model,
      inputTokens,
      outputTokens
    });
    
    // If shopId is provided, update metrics in shop document
    if (shopId) {
      await recordMetricsCreditUsage(userId, shopId, creditsUsed);
    }
    
    console.log('Credit usage recorded successfully using optimized service');
    return true;
  } catch (error) {
    console.error('Error recording credit usage:', error);
    return false;
  }
};

// Get user's credit status
export const getUserCreditStatus = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    console.log('User data for credit status:', {
      userId,
      hasSubscription: !!userData?.subscription,
      isVIP: !!userData?.isVIP,
      vipPlan: userData?.vipPlan,
      hasVipSubscription: !!userData?.vipSubscription
    });
    
    // Get user's subscription if available
    let subscription = userData?.subscription || null;
    
    // If we have a subscription with a type, fetch the plan name from Firestore
    if (subscription && subscription.type) {
      try {
        console.log(`Fetching plan name for subscription type: ${subscription.type}`);
        const planDoc = await getDoc(doc(db, 'subscriptionPlans', subscription.type));
        
        if (planDoc.exists()) {
          const planData = planDoc.data();
          console.log(`Found plan in Firestore: ${planData.name}`);
          
          // Update the subscription with the correct plan name
          subscription = {
            ...subscription,
            planName: planData.name
          };
        } else {
          console.log(`Plan not found in Firestore for type: ${subscription.type}`);
        }
      } catch (error) {
        console.error('Error fetching plan from Firestore:', error);
      }
    }
    
    // Check for VIP status and plan
    const isVIP = userData?.isVIP || false;
    const vipPlan = userData?.vipPlan || null;
    let vipSubscription = userData?.vipSubscription || null;
    
    // If user has VIP status and a VIP plan, fetch the plan name from Firestore
    if (isVIP && vipPlan) {
      try {
        console.log(`Fetching plan name for VIP plan: ${vipPlan}`);
        const planDoc = await getDoc(doc(db, 'subscriptionPlans', vipPlan));
        
        if (planDoc.exists()) {
          const planData = planDoc.data();
          console.log(`Found VIP plan in Firestore: ${planData.name}`);
          
          // If vipSubscription exists, update it with the correct plan name
          if (vipSubscription) {
            vipSubscription = {
              ...vipSubscription,
              planName: planData.name
            };
          } else {
            // Create a basic vipSubscription object if it doesn't exist
            vipSubscription = {
              planName: planData.name,
              nextBillingDate: null,
              endDate: userData?.vipEndDate || null,
              monthlyCredits: planData.monthlyCredits || 0
            };
          }
        } else {
          console.log(`VIP Plan not found in Firestore for type: ${vipPlan}`);
        }
      } catch (error) {
        console.error('Error fetching VIP plan from Firestore:', error);
      }
    }
    
    if (isVIP && vipSubscription) {
      console.log('VIP subscription details:', {
        planName: vipSubscription.planName,
        nextBillingDate: vipSubscription.nextBillingDate,
        endDate: vipSubscription.endDate,
        monthlyCredits: vipSubscription.monthlyCredits
      });
    }
    
    const result = {
      credits: userData?.credits || { total: 0, used: 0, monthly: 0, flex: 0 },
      pushHistory: userData?.pushHistory || { totalPushes: 0 },
      subscription: subscription,
      vipSubscription: vipSubscription,
      isVIP,
      vipPlan
    };
    
    console.log('Returning credit status:', result);
    return result;
  } catch (error) {
    console.error('Error getting user credit status:', error);
    throw error;
  }
};

// Check if user has enough credits
export const hasEnoughCredits = async (userId, requiredCredits = 1) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    // VIP users always have enough credits
    if (userData?.isVIP && userData?.vipPlan) {
      console.log('VIP user detected with plan:', userData.vipPlan);
      return true;
    }
    
    if (!userData?.credits) {
      console.log('No credits found for user');
      return false;
    }

    const { monthly = 0, flex = 0 } = userData.credits;
    const availableCredits = monthly + flex;
    
    console.log('Credit check:', {
      monthly,
      flex,
      available: availableCredits,
      required: requiredCredits
    });
    
    return availableCredits >= requiredCredits;
  } catch (error) {
    console.error('Error checking credits:', error);
    throw error;
  }
};

// Deduct credits
export const deductCredits = async (userId, creditsToDeduct = 1) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    // VIP users should still have credits deducted
    // Just record the VIP status for logging purposes
    if (userData?.isVIP && userData?.vipPlan) {
      console.log('VIP user detected with plan:', userData.vipPlan);
    }
    
    if (!userData?.credits) {
      console.log('No credits found for user');
      return false;
    }

    const { monthly = 0, flex = 0, total = 0 } = userData.credits;
    const availableCredits = monthly + flex;
    
    if (availableCredits < creditsToDeduct) {
      console.log('Insufficient credits:', {
        monthly,
        flex,
        available: availableCredits,
        required: creditsToDeduct
      });
      return false;
    }

    console.log('Deducting credits:', {
      monthly,
      flex,
      available: availableCredits,
      required: creditsToDeduct
    });

    let remainingToDeduct = creditsToDeduct;
    let updates = {
      'credits.used': increment(creditsToDeduct)
    };
    
    // First deduct from monthly credits
    if (monthly > 0) {
      const monthlyDeduction = Math.min(monthly, remainingToDeduct);
      updates['credits.monthly'] = increment(-monthlyDeduction);
      remainingToDeduct -= monthlyDeduction;
    }
    
    // Then deduct from flex credits if needed
    if (remainingToDeduct > 0 && flex > 0) {
      const flexDeduction = Math.min(flex, remainingToDeduct);
      updates['credits.flex'] = increment(-flexDeduction);
      remainingToDeduct -= flexDeduction;
    }
    
    // For backward compatibility, also update the total field
    updates['credits.total'] = Math.max(0, total - creditsToDeduct);

    await updateDoc(userRef, updates);

    return true;
  } catch (error) {
    console.error('Error deducting credits:', error);
    throw error;
  }
};

// Deduct flex credits from a user's account
export const deductFlexCredits = async (userId, amount) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    if (!amount || amount <= 0) {
      throw new Error('Invalid credit amount');
    }

    // Get the user's current credit balance
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const currentCredits = userData.credits || 0;

    // Check if the user has enough credits
    if (currentCredits < amount) {
      throw new Error(`Insufficient credits. Required: ${amount}, Available: ${currentCredits}`);
    }

    // Deduct the credits
    const newCreditBalance = currentCredits - amount;
    await updateDoc(userRef, {
      credits: newCreditBalance,
      updatedAt: new Date().toISOString()
    });

    // Record the credit usage - ensure userId is passed as the first parameter
    try {
      if (userId) {
        // Try to record credit usage, but don't fail if it doesn't work
        await recordCreditUsage(userId, 'ai_model', {
          amount,
          timestamp: new Date().toISOString(),
          type: 'deduction',
          reason: 'AI model usage',
          userId // Add userId to the params as well for extra safety
        }).catch(err => {
          // Log the error but don't throw it - this prevents the credit deduction from failing
          console.warn('Error recording credit usage metrics (non-critical):', err.message);
        });
      } else {
        console.error('Cannot record credit usage: userId is undefined');
      }
    } catch (metricError) {
      // Log the error but don't throw it - this prevents the credit deduction from failing
      console.warn('Error recording credit usage metrics (non-critical):', metricError.message);
    }

    return {
      success: true,
      deductedAmount: amount,
      remainingCredits: newCreditBalance
    };
  } catch (error) {
    console.error('Error deducting flex credits:', error);
    throw error;
  }
};

// Get detailed credit statistics
export const getCreditStats = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    if (!userData) return null;

    console.log('Raw user data:', userData);
    const credits = userData.credits || { total: 0, used: 0, monthly: 0, flex: 0 };
    console.log('Credits data:', credits);
    
    const pushHistory = userData.pushHistory || { totalPushes: 0, monthlyPushes: {}, shopPushes: {} };
    
    // Calculate monthly stats
    const currentMonth = new Date().toISOString().slice(0, 7);
    const lastMonth = new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString().slice(0, 7);
    
    // Get last 6 months for trend
    const last6Months = Array.from({length: 6}, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (5 - i));
      return date.toISOString().slice(0, 7);
    });

    const monthlyTrend = last6Months.map(month => ({
      month,
      pushes: pushHistory.monthlyPushes[month] || 0
    }));
    
    // Get last 14 days for daily trend
    const last14Days = Array.from({length: 14}, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (13 - i));
      return date.toISOString().slice(0, 10); // YYYY-MM-DD format
    });
    
    // Extract daily push data from pushHistory if available, or create empty data
    const dailyTrend = last14Days.map(date => ({
      date,
      pushes: (pushHistory.dailyPushes && pushHistory.dailyPushes[date]) || 0
    }));

    // Calculate daily average for current month
    const currentMonthPushes = pushHistory.monthlyPushes[currentMonth] || 0;
    const daysInMonth = new Date().getDate();
    const dailyAverage = currentMonthPushes / daysInMonth;

    // Get subscription info
    const subscription = userData.subscription || null;
    const nextRefill = subscription ? new Date(subscription.nextBillingDate) : null;
    const daysUntilRefill = nextRefill 
      ? Math.ceil((nextRefill.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) 
      : null;
    
    // Get credit usage by type from creditStats collection
    let templateCredits = 0;
    let imageCredits = 0;
    let otherCredits = 0;
    let totalCreditsUsed = 0;
    let monthlyUsed = 0;
    
    try {
      // Query creditStats collection to get usage by type
      const creditStatsRef = collection(db, 'creditStats');
      const q = query(
        creditStatsRef,
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      
      // Process query results
      querySnapshot.forEach(doc => {
        const usage = doc.data();
        const creditsUsed = usage.creditsUsed || 1;
        const usageType = usage.usageType || 'other';
        const timestamp = usage.timestamp;
        
        // Skip records with isTypeUpdate flag to avoid double counting
        if (usage.isTypeUpdate) {
          return;
        }
        
        // Add to total
        totalCreditsUsed += creditsUsed;
        
        // Add to usage by type
        if (usageType === 'template') {
          templateCredits += creditsUsed;
        } else if (usageType === 'image') {
          imageCredits += creditsUsed;
        } else {
          otherCredits += creditsUsed;
        }
        
        // Check if this is from the current month
        if (timestamp) {
          const usageDate = new Date(timestamp);
          const usageMonth = `${usageDate.getFullYear()}-${String(usageDate.getMonth() + 1).padStart(2, '0')}`;
          
          if (usageMonth === currentMonth) {
            monthlyUsed += creditsUsed;
          }
        }
      });
      
      console.log('Credit usage from creditStats:', {
        template: templateCredits,
        image: imageCredits,
        other: otherCredits,
        total: totalCreditsUsed,
        monthly: monthlyUsed
      });
    } catch (statsError) {
      console.error('Error getting credit usage stats:', statsError);
      // Continue with the function even if this part fails
    }

    return {
      available: credits.monthly + credits.flex,
      monthly: credits.monthly,
      flex: credits.flex,
      total: credits.total, // Legacy field
      used: credits.used,
      totalUsed: totalCreditsUsed > 0 ? totalCreditsUsed : credits.used, // Use the calculated total if available
      monthlyUsed: monthlyUsed, // Add monthly usage from creditStats
      templateCredits: templateCredits, // Add template credits
      imageCredits: imageCredits, // Add image credits
      otherCredits: otherCredits, // Add other credits
      totalPushes: pushHistory.totalPushes || 0,
      thisMonth: pushHistory.monthlyPushes[currentMonth] || 0,
      lastMonth: pushHistory.monthlyPushes[lastMonth] || 0,
      monthlyTrend,
      dailyTrend, // Add daily trend data
      dailyAverage: Math.round(dailyAverage * 100) / 100,
      shopBreakdown: pushHistory.shopPushes || {},
      subscription,
      daysUntilRefill,
      remainingCredits: credits.monthly + credits.flex // Add remaining credits for the dashboard
    };
  } catch (error) {
    console.error('Error getting credit stats:', error);
    throw error;
  }
};

// Check for and process subscription renewal if needed
export const checkAndProcessRenewal = async (userId) => {
  try {
    console.log(`Checking credit renewal for user: ${userId}`);
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    if (!userData) {
      console.log(`No user data found for ${userId}`);
      return false;
    }
    
    // Check for regular subscription first
    const hasActiveSubscription = userData?.subscription && userData.subscription.status === 'active';
    const hasVIPSubscription = userData?.isVIP && userData?.vipSubscription;
    
    console.log(`User ${userId} subscription status: Active regular subscription: ${hasActiveSubscription}, VIP: ${hasVIPSubscription}`);
    
    if (!hasActiveSubscription && !hasVIPSubscription) {
      console.log(`User ${userId} has no active subscriptions`);
      return false; // No active subscription or VIP status
    }
    
    const now = new Date();
    let shouldRenew = false;
    let monthlyCredits = 0;
    let nextCreditResetDate = null;
    let subscriptionType = null;
    
    // Check if VIP subscription has expired
    if (hasVIPSubscription && userData.vipSubscription.endDate) {
      const endDate = new Date(userData.vipSubscription.endDate);
      console.log(`VIP subscription end date: ${endDate.toISOString()}, Current date: ${now.toISOString()}`);
      
      if (now >= endDate) {
        console.log(`VIP subscription has expired for user ${userId}`);
        // Import dynamically to avoid circular dependency
        const { removeUserVIPStatus } = await import('./subscriptionService');
        await removeUserVIPStatus(userId);
        
        // If user also has a regular subscription, we'll continue with that
        if (!hasActiveSubscription) {
          console.log(`No regular subscription after VIP removal for user ${userId}`);
          return true; // VIP status removed, no regular subscription to process
        }
      }
    }
    
    // Check regular subscription renewal
    if (hasActiveSubscription) {
      subscriptionType = 'regular';
      // First check if we have the dedicated nextCreditResetDate field
      if (userData.subscription.nextCreditResetDate) {
        nextCreditResetDate = new Date(userData.subscription.nextCreditResetDate);
        console.log(`Using nextCreditResetDate for regular subscription: ${nextCreditResetDate.toISOString()}`);
      } 
      // Fall back to nextBillingDate for older subscriptions
      else if (userData.subscription.nextBillingDate) {
        nextCreditResetDate = new Date(userData.subscription.nextBillingDate);
        console.log(`Using nextBillingDate for regular subscription: ${nextCreditResetDate.toISOString()}`);
      }
      // If neither exists, calculate based on subscription start date or last refill
      else {
        // For both monthly and yearly subscriptions, calculate based on start date or last refill
        let baseDate;
        
        if (userData.credits?.lastRefillDate) {
          baseDate = new Date(userData.credits.lastRefillDate);
          console.log(`Using lastRefillDate as base: ${baseDate.toISOString()}`);
        } else if (userData.subscription.currentPeriodStart) {
          baseDate = new Date(userData.subscription.currentPeriodStart);
          console.log(`Using currentPeriodStart as base: ${baseDate.toISOString()}`);
        } else if (userData.subscription.periodStartTimestamp) {
          baseDate = new Date(userData.subscription.periodStartTimestamp * 1000);
          console.log(`Using periodStartTimestamp as base: ${baseDate.toISOString()}`);
        } else {
          // If no dates available, use subscription creation date or now
          baseDate = userData.subscription.createdAt ? 
                    new Date(userData.subscription.createdAt) : 
                    new Date();
          console.log(`Using fallback date as base: ${baseDate.toISOString()}`);
        }
        
        // Calculate next reset date (1 month from base date)
        nextCreditResetDate = new Date(baseDate);
        nextCreditResetDate.setMonth(nextCreditResetDate.getMonth() + 1);
        
        // If this date is in the past, keep adding months until we get a future date
        while (nextCreditResetDate <= now) {
          nextCreditResetDate.setMonth(nextCreditResetDate.getMonth() + 1);
          console.log(`Adjusted nextCreditResetDate to future: ${nextCreditResetDate.toISOString()}`);
        }
      }
      
      shouldRenew = nextCreditResetDate <= now;
      monthlyCredits = userData.subscription.monthlyCredits || 0;
      
      console.log(`Regular subscription should renew: ${shouldRenew}, Next reset: ${nextCreditResetDate.toISOString()}, Credits: ${monthlyCredits}`);
    }
    
    // Check VIP subscription renewal
    if (hasVIPSubscription && !shouldRenew) {
      subscriptionType = 'vip';
      // First check if we have the dedicated nextCreditResetDate field
      if (userData.vipSubscription.nextCreditResetDate) {
        nextCreditResetDate = new Date(userData.vipSubscription.nextCreditResetDate);
        console.log(`Using nextCreditResetDate for VIP: ${nextCreditResetDate.toISOString()}`);
      }
      // Fall back to nextBillingDate for older VIP subscriptions
      else if (userData.vipSubscription.nextBillingDate) {
        nextCreditResetDate = new Date(userData.vipSubscription.nextBillingDate);
        console.log(`Using nextBillingDate for VIP: ${nextCreditResetDate.toISOString()}`);
      }
      // If neither exists, calculate based on VIP start date or last refill
      else {
        let baseDate;
        
        if (userData.credits?.lastRefillDate) {
          baseDate = new Date(userData.credits.lastRefillDate);
          console.log(`Using lastRefillDate as base for VIP: ${baseDate.toISOString()}`);
        } else if (userData.vipSubscription.startDate) {
          baseDate = new Date(userData.vipSubscription.startDate);
          console.log(`Using startDate as base for VIP: ${baseDate.toISOString()}`);
        } else {
          baseDate = new Date(); // Fallback to current date
          console.log(`Using current date as base for VIP: ${baseDate.toISOString()}`);
        }
        
        // Calculate next reset date (1 month from base date)
        nextCreditResetDate = new Date(baseDate);
        nextCreditResetDate.setMonth(nextCreditResetDate.getMonth() + 1);
        
        // If this date is in the past, keep adding months until we get a future date
        while (nextCreditResetDate <= now) {
          nextCreditResetDate.setMonth(nextCreditResetDate.getMonth() + 1);
          console.log(`Adjusted VIP nextCreditResetDate to future: ${nextCreditResetDate.toISOString()}`);
        }
      }
      
      shouldRenew = nextCreditResetDate <= now;
      monthlyCredits = userData.vipSubscription.monthlyCredits || 0;
      
      console.log(`VIP subscription should renew: ${shouldRenew}, Next reset: ${nextCreditResetDate.toISOString()}, Credits: ${monthlyCredits}`);
    }
    
    // Process renewal if needed
    if (shouldRenew) {
      console.log(`Processing monthly credit renewal for user: ${userId}, Subscription type: ${subscriptionType}, Credits: ${monthlyCredits}`);
      
      // Calculate next credit reset date (1 month from current reset date)
      const nextReset = new Date(nextCreditResetDate);
      nextReset.setMonth(nextReset.getMonth() + 1);
      
      // Update data based on subscription type
      const updateData = {
        'credits.monthly': monthlyCredits,
        'credits.lastRefillDate': now.toISOString()
      };
      
      // Update the appropriate nextCreditResetDate field
      if (subscriptionType === 'regular') {
        updateData['subscription.nextCreditResetDate'] = nextReset.toISOString();
      } else if (subscriptionType === 'vip') {
        updateData['vipSubscription.nextCreditResetDate'] = nextReset.toISOString();
      }
      
      console.log(`Updating user ${userId} with data:`, updateData);
      
      // Apply the updates
      await updateDoc(userRef, updateData);
      console.log(`Successfully updated credits for user ${userId}`);
      
      return true;
    }
    
    console.log(`No credit renewal needed for user ${userId}`);
    return false;
  } catch (error) {
    console.error('Error checking for subscription renewal:', error);
    return false;
  }
};

// Refund credits for failed operations
export const refundCredits = async (userId, creditsToRefund = 1) => {
  try {
    console.log(`Refunding ${creditsToRefund} credits to user ${userId}`);
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    if (!userData?.credits) {
      console.log('No credits found for user to refund');
      return false;
    }

    // Add credits back to the monthly pool
    const updates = {
      'credits.monthly': increment(creditsToRefund),
      'credits.used': increment(-creditsToRefund), // Reduce used count
      'credits.total': increment(creditsToRefund) // For backward compatibility
    };

    await updateDoc(userRef, updates);
    
    // Record the refund for tracking
    try {
      const creditRefundsCollection = collection(db, 'creditRefunds');
      await addDoc(creditRefundsCollection, {
        userId,
        timestamp: new Date().toISOString(),
        creditsRefunded: creditsToRefund,
        reason: 'Shopify push error'
      });
    } catch (recordError) {
      console.error('Error recording credit refund:', recordError);
      // Continue even if recording fails, as the credits have been refunded
    }

    console.log(`Successfully refunded ${creditsToRefund} credits to user ${userId}`);
    return true;
  } catch (error) {
    console.error('Error refunding credits:', error);
    return false;
  }
};

/**
 * Refund credits for template executions
 * @param {string} userId - The user ID
 * @param {Array|Object} executionMetadata - Metadata about the template executions
 * @returns {Promise<boolean>} - Whether the refund was successful
 */
export const refundCreditsForTemplateExecution = async (userId, executionMetadata) => {
  try {
    if (!userId) {
      console.error('User ID is required to refund template execution credits');
      return false;
    }

    if (!executionMetadata) {
      console.error('Execution metadata is required to refund template execution credits');
      return false;
    }

    // Handle both single execution metadata and arrays of execution metadata
    const metadataArray = Array.isArray(executionMetadata) ? executionMetadata : [executionMetadata];
    
    if (metadataArray.length === 0) {
      console.log('No template executions to refund');
      return true;
    }
    
    console.log(`Refunding credits for ${metadataArray.length} template executions`);
    
    // Calculate total credits to refund
    let totalCreditsToRefund = 0;
    const refundDetails = [];
    
    metadataArray.forEach(metadata => {
      if (metadata && metadata.totalCreditCost) {
        totalCreditsToRefund += metadata.totalCreditCost;
        refundDetails.push({
          templateId: metadata.templateId,
          templateName: metadata.templateName,
          productId: metadata.productId,
          executionId: metadata.executionId,
          credits: metadata.totalCreditCost
        });
      }
    });
    
    if (totalCreditsToRefund === 0) {
      console.log('No credits to refund');
      return true;
    }
    
    // Create a credit usage record for the refund
    // Use the optimized credit service for tracking refunds
    const refundData = {
      userId,
      credits: totalCreditsToRefund,
      timestamp: serverTimestamp(),
      type: 'template_execution_refund',
      status: 'refunded',
      refundDetails,
      metadata: {
        executionCount: metadataArray.length,
        reason: 'push_to_shopify_failed'
      }
    };

    // Use a transaction to ensure both operations succeed or fail together
    await runTransaction(db, async (transaction) => {
      const userRef = doc(db, 'users', userId);
      const userDoc = await transaction.get(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }
      
      const userData = userDoc.data();
      if (!userData.credits) {
        throw new Error('No credits found for user');
      }
      
      // Update the user's credits - add back to the appropriate pools
      // For simplicity, we'll add them back to flex credits
      const updates = {
        'credits.flex': increment(totalCreditsToRefund),
        'credits.used': increment(-totalCreditsToRefund) // Decrement used credits
      };
      
      // Apply the updates
      transaction.update(userRef, updates);
      
      // We'll record the refund using the optimized service after the transaction
    });

    // Record the refund using the optimized credit service
    await recordOptimizedCreditUsage(userId, -totalCreditsToRefund, {
      type: 'template_execution_refund',
      executionIds: metadataArray.map(m => m.executionId).filter(Boolean),
      templateIds: metadataArray.map(m => m.templateId).filter(Boolean),
      reason: 'Template execution refund'
    });

    console.log(`Successfully refunded ${totalCreditsToRefund} credits to user ${userId} for ${metadataArray.length} template executions`);
    return true;
  } catch (error) {
    console.error('Error refunding credits for template executions:', error);
    return false;
  }
};

/**
 * Check if a user has enough credits for an AI model operation
 * @param {string} userId - The user ID
 * @param {number} creditCost - The number of credits required
 * @returns {Promise<boolean>} - Whether the user has enough credits
 */
export const hasEnoughCreditsForAIModel = async (userId, creditCost) => {
  try {
    if (!userId) {
      console.error('User ID is required to check credits');
      return false;
    }

    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      console.error('User document not found');
      return false;
    }

    const userData = userDoc.data();
    
    // Check if credits object exists
    if (!userData.credits) {
      console.error('No credits found for user');
      return false;
    }
    
    // Calculate total available credits (monthly + flex)
    const monthlyCredits = userData.credits.monthly || 0;
    const flexCredits = userData.credits.flex || 0;
    const totalAvailableCredits = monthlyCredits + flexCredits;

    console.log(`User ${userId} has ${monthlyCredits} monthly credits and ${flexCredits} flex credits, needs ${creditCost}`);
    return totalAvailableCredits >= creditCost;
  } catch (error) {
    console.error('Error checking if user has enough credits for AI model:', error);
    return false;
  }
};

/**
 * Deduct credits for AI model usage
 * @param {string} userId - The user ID
 * @param {number} creditCost - The number of credits to deduct
 * @param {Object} metadata - Additional metadata about the operation
 * @returns {Promise<void>}
 */
export const deductCreditsForAIModel = async (userId, creditCost, metadata = {}) => {
  try {
    if (!userId) {
      throw new Error('User ID is required to deduct credits');
    }

    // First check if the user has enough credits
    const hasCredits = await hasEnoughCreditsForAIModel(userId, creditCost);
    if (!hasCredits) {
      throw new Error(`Not enough credits. You need ${creditCost} credits to process this template.`);
    }

    const userRef = doc(db, 'users', userId);
    
    // Determine the usageType based on the metadata
    let usageType = 'other';
    if (metadata.type === 'sizechart_generation' || metadata.type === 'sizechart_comparison') {
      usageType = 'sizechart';
    } else if (metadata.templateId) {
      usageType = 'template';
    } else if (metadata.type === 'image_generation' || metadata.type === 'image_processing') {
      usageType = 'image';
    }
    
    const usageData = {
      userId,
      credits: creditCost,
      timestamp: serverTimestamp(),
      type: 'ai_model',
      usageType: usageType, // Add usageType field for proper categorization
      status: 'deducted',
      ...metadata
    };

    // Use a transaction to ensure both operations succeed or fail together
    await runTransaction(db, async (transaction) => {
      // IMPORTANT: Perform ALL reads first before ANY writes in a Firestore transaction
      
      // 1. Read the user document
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }
      
      const userData = userDoc.data();
      if (!userData.credits) {
        throw new Error('No credits found for user');
      }
      
      // 2. Read the shop document if needed (BEFORE any writes)
      let shopDoc = null;
      if (metadata.shopId && metadata.templateId) {
        const shopRef = doc(db, 'shops', metadata.shopId);
        shopDoc = await transaction.get(shopRef);
        // We'll check if it exists later when we need to use it
      }
      
      // Calculate credit deductions
      const monthlyCredits = userData.credits.monthly || 0;
      const flexCredits = userData.credits.flex || 0;
      const totalAvailableCredits = monthlyCredits + flexCredits;
      
      if (totalAvailableCredits < creditCost) {
        throw new Error(`Not enough credits. You have ${monthlyCredits} monthly credits and ${flexCredits} flex credits, but need ${creditCost} credits.`);
      }
      
      // Determine how many credits to deduct from each pool
      let monthlyToDeduct = Math.min(monthlyCredits, creditCost);
      let flexToDeduct = creditCost - monthlyToDeduct;
      
      // Prepare updates - NOW we can start the writes
      const updates = {
        'credits.used': increment(creditCost) // Increment total used credits
      };
      
      // Deduct from monthly credits first
      if (monthlyToDeduct > 0) {
        updates['credits.monthly'] = monthlyCredits - monthlyToDeduct;
        updates['credits.total'] = Math.max(0, (userData.credits.total || 0) - monthlyToDeduct); // For backward compatibility
      }
      
      // Then deduct from flex credits if needed
      if (flexToDeduct > 0) {
        updates['credits.flex'] = flexCredits - flexToDeduct;
      }
      
      // Apply the user credit updates
      transaction.update(userRef, updates);
    });

    // Use the optimized credit service for tracking only
    await recordCreditUsage(userId, creditCost, {
      ...metadata,
      usageType,
      type: 'ai_model',
      status: 'deducted'
    });

    console.log(`Successfully deducted ${creditCost} credits from user ${userId} for AI model usage`);
    
    // Record template usage metrics if this was for a template execution
    if (metadata.shopId && metadata.templateId) {
      try {
        const { recordTemplateUsage } = await import('./metricsService');
        await recordTemplateUsage(
          userId,
          metadata.shopId,
          metadata.templateId,
          true, // Assuming success since we got here
          metadata.productId || null,
          creditCost
        );
        console.log(`Recorded template usage metrics for template ${metadata.templateId} with ${creditCost} credits`);
      } catch (metricError) {
        // Don't fail the whole operation if metrics recording fails
        console.error('Error recording template usage metrics:', metricError);
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error deducting credits for AI model:', error);
    throw error;
  }
};
